import { createReducer, on } from '@ngrx/store';
import {
  getAllowanceRecommendationFail,
  getAllowanceRecommendationSuccess,
  getAllTreatsRecommendationQuantitySuccess,
  getAllWetRecommendationQuantitySuccess,
  setAllowanceCurrentBlockEditing,
  setAllowanceDefaultRecommendation,
  setAllowanceLoadingRecommendation,
  setAllowanceRecommendationForGestation,
} from './actions/allowance-recommendation.actions';
import { resetAllowance, resetAllowanceParams, resetAllowanceStepToFirst, setAllowanceStep } from './actions/allowance-steps.actions';
import { initialAllowanceState } from './allowance.state';
import {
  convertAllowanceFlow,
  resetAllowanceFlow,
  resetAllowanceSelectedProductId,
  setAllowanceNavigationRoutes,
  setAllowanceParams,
  setProductSelectionIncentive,
} from './actions';

/**
 * Allowance reducer
 */

export const allowanceReducer = createReducer(
  initialAllowanceState,

  /** allowance-consultation.actions */
  on(setAllowanceParams, (state, action) => ({
    ...state,
    allowanceParams: action.value,
  })),
  on(resetAllowanceSelectedProductId, (state, action) => {
    if (!state.allowanceParams?.selectedProductIds) {
      return state;
    }

    const { selectedProductIds } = state.allowanceParams;
    const productId = action.productId;

    // Find which key contains the product ID
    let keyToRemove: keyof typeof selectedProductIds | null = null;

    if (selectedProductIds.dry === productId) {
      keyToRemove = 'dry';
    } else if (selectedProductIds.wet === productId) {
      keyToRemove = 'wet';
    } else if (selectedProductIds.treats === productId) {
      keyToRemove = 'treats';
    }

    if (!keyToRemove) {
      return state; // Product ID not found, return unchanged state
    }

    return {
      ...state,
      allowanceParams: {
        ...state.allowanceParams,
        selectedProductIds: {
          ...selectedProductIds,
          [keyToRemove]: undefined,
        },
      },
    };
  }),
  on(setAllowanceNavigationRoutes, (state, { allowanceNavigationRoutes }) => ({
    ...state,
    allowanceNavigationRoutes: {
      editPatientRoute: allowanceNavigationRoutes.editPatientRoute,
      editDiagnosticRoute: allowanceNavigationRoutes.editDiagnosticRoute,
      editSelectionRoute: allowanceNavigationRoutes.editSelectionRoute,
    },
  })),
  on(setProductSelectionIncentive, (state, { productSelectionIncentive }) => ({
    ...state,
    productSelectionIncentive: {
      showDry: productSelectionIncentive.showDry,
      showWet: productSelectionIncentive.showWet,
      showTreats: productSelectionIncentive.showTreats,
    },
  })),
  on(convertAllowanceFlow, (state, action) => ({
    ...state,
    allowanceParams: { ...state.allowanceParams, tool: action.flow },
  })),
  on(resetAllowanceFlow, (state, action) => ({
    ...state,
    allowanceParams: { ...state.allowanceParams, tool: action.coreTool },
  })),

  /** allowance-recommendation.actions */
  on(getAllowanceRecommendationSuccess, (state, action) => ({
    ...state,
    nutritionRecommendation: action.recommendation,
    loadingRecommendation: false,
  })),
  on(getAllowanceRecommendationFail, (state) => ({
    ...state,
    loadingRecommendation: false,
  })),
  on(getAllWetRecommendationQuantitySuccess, (state, action) => ({
    ...state,
    allWetQuantityPerDay: action.allWetQuantityPerDay,
  })),
  on(getAllTreatsRecommendationQuantitySuccess, (state, action) => ({
    ...state,
    allTreatsQuantityPerDay: action.allTreatsQuantityPerDay,
  })),
  on(setAllowanceDefaultRecommendation, (state, action) => ({
    ...state,
    defaultNutritionRecommendation: action.recommendation,
  })),
  on(setAllowanceRecommendationForGestation, (state, action) => ({
    ...state,
    gestationNutritionRecommendation: action.recommendation,
  })),
  on(setAllowanceLoadingRecommendation, (state, action) => ({
    ...state,
    loadingRecommendation: action.value,
  })),
  on(setAllowanceCurrentBlockEditing, (state, { block, value }) => ({
    ...state,
    blockCurrentlyEditing: { ...state.blockCurrentlyEditing, [block]: value },
  })),

  /** allowance-steps.actions */
  on(setAllowanceStep, (state, { step }) => ({
    ...state,
    step,
  })),
  on(resetAllowanceStepToFirst, (state) => ({
    ...state,
    step: initialAllowanceState.step,
  })),
  on(resetAllowanceParams, (state) => ({
    ...state,
    allowanceParams: initialAllowanceState.allowanceParams,
  })),
  on(resetAllowance, () => initialAllowanceState),
);
